<script setup lang="ts">
import { computed, ref } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import type { Product } from '@bf/domain'
import EntityCard from '../components/EntityCard.vue'
import { useShowroomStore } from '../store/showroom'

const store = useShowroomStore()
const route = useRoute()
type HardwareFilter = 'all' | Product['productCategory']
const hardwareFilter = ref<HardwareFilter>('all')

const hardwareFilters: Array<{ id: HardwareFilter; label: string }> = [
  { id: 'all', label: '全部' },
  { id: 'cabinet', label: '机柜' },
  { id: 'ddc-controller', label: 'DDC / 控制器' },
  { id: 'terminal-device', label: '末端设备' }
]

const section = computed(() => String(route.params.section))
const configuration = computed(() => {
  if (section.value === 'hardware') {
    return {
      eyebrow: 'HARDWARE LIBRARY',
      title: '硬件产品库',
      description: '集中查阅机柜、DDC 控制器和末端设备资料；专业选型请进入对应功能页面。'
    }
  }
  if (section.value === 'software') {
    return {
      eyebrow: 'SOFTWARE CAPABILITIES',
      title: '软件完整风格方案',
      description: '8 套客户可选视觉方案，每套均包含 10 个业务画面；通过监控、能耗、运维、趋势、报警、报表、用户、配置、流程图与群控画面逐页介绍软件功能和客户价值。'
    }
  }
  return {
    eyebrow: 'PROJECT REFERENCES',
    title: '工程案例',
    description: '只使用经过授权、技术审核和 OT 脱敏的项目材料。'
  }
})

const entities = computed(() =>
  store.entities.value
    .filter((entity) => {
      if (section.value === 'hardware') {
        return (
          entity.contentType === 'product' &&
          (hardwareFilter.value === 'all' ||
            entity.productCategory === hardwareFilter.value)
        )
      }
      if (section.value === 'software') {
        return entity.contentType === 'software-style'
      }
      return entity.contentType === 'project-case'
    })
    .sort((a, b) => a.sortOrder - b.sortOrder)
)
</script>

<template>
  <section class="catalog-intro">
    <p class="eyebrow">{{ configuration.eyebrow }}</p>
    <h2>{{ configuration.title }}</h2>
    <p>{{ configuration.description }}</p>
    <div class="catalog-count">
      <strong>{{ entities.length }}</strong>
      <span>项当前内容</span>
    </div>
  </section>

  <div v-if="section === 'hardware'" class="catalog-toolbar">
    <div class="filter-group" role="group" aria-label="硬件产品库分类筛选">
      <button
        v-for="filter in hardwareFilters"
        :key="filter.id"
        type="button"
        :class="{ active: hardwareFilter === filter.id }"
        :aria-pressed="hardwareFilter === filter.id"
        @click="hardwareFilter = filter.id"
      >
        {{ filter.label }}
      </button>
    </div>
    <RouterLink class="button button-primary" :to="{ name: 'cabinet-generator' }">
      进入机柜生成器
    </RouterLink>
  </div>

  <div v-if="entities.length" class="entity-grid">
    <EntityCard
      v-for="entity in entities"
      :key="entity.id"
      :entity="entity"
      @add="store.addToDraft"
    />
  </div>
  <div v-else class="empty-state">
    <strong>该分类暂无内容</strong>
    <p>材料通过审核后会显示在这里。</p>
  </div>
</template>
